import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, from, of } from 'rxjs';

import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { switchMap, tap } from 'rxjs/operators';
import { BackboneAdapterService } from './backbone-adapter.service';
import { BaseNotificationModalComponent } from './base-notification-modal/base-notification-modal.component';
import { GenericNotificationComponent } from './generic-notification/generic-notification.component';
import { ModalService } from './modal.service';
import { RequireAdapterService } from './require-adapter.service';
import { FeedbackRequestType } from './types/feedback-request-type';
import { NotificationDto } from './types/notification-dto';
import { NotificationEventType } from './types/notification-event-type';
import { NOTIFICATION_COMPONENT_MAP, NOTIFICATION_MODAL_ADDITIONAL_DATA_MAP } from './notification-components.map';
import { OeAppService } from './oe-app.service';
import { OeAppPersonService } from './oe-app-person.service';

@Injectable()
export class NotificationService {
  public onReceived: Subject<NotificationDto> = new Subject();
  private _readDateSet$ = new Subject<NotificationDto['id']>();
  public readDateSet$ = this._readDateSet$.asObservable();

  private notificationListener;

  private modalRef: NgbModalRef;

  constructor(
    private req: RequireAdapterService,
    private backbone: BackboneAdapterService,
    private http: HttpClient,
    private modalService: ModalService,
    private oeApp: OeAppService,
    private oeAppPersonService: OeAppPersonService
  ) {}

  init() {
    this.notificationListener = this.req.loadNotificationListener();
    this.backbone.addListener('liveclass:class-ready', (notification: NotificationDto) => {
      this.onReceived.next(notification);
    });

    window.addEventListener('read-notification-event', (event: CustomEvent) => {
      this._readDateSet$.next(event.detail.data);
    });

    window.addEventListener('notification-event', (event: CustomEvent) => {
      const { data } = event.detail;
      const modalData = { ...data, ...NOTIFICATION_MODAL_ADDITIONAL_DATA_MAP[data.type] };
      let component = NOTIFICATION_COMPONENT_MAP[data.type] || GenericNotificationComponent;
      if (this.isAskATeacherOrWritingFeedback(data)) {
        component = GenericNotificationComponent;
      }
      if (data.type === NotificationEventType.LIVE_CLASS_SURVEY) {
        // Flag added to distinguish between the modal opened from the new notification event and the one manually opened by the user
        modalData.isNewNotification = true;
      }
      this.closeModal();
      this.openNotificationModal(component, modalData);
    });
  }

  private isAskATeacherOrWritingFeedback(notification: NotificationDto): boolean {
    return (
      notification.type === NotificationEventType.TEACHER_FEEDBACK &&
      [FeedbackRequestType.ASK_A_TEACHER, FeedbackRequestType.WRITING_FEEDBACK].includes(
        notification.title as FeedbackRequestType
      )
    );
  }

  openNotificationModal(
    modalComponent: typeof NOTIFICATION_COMPONENT_MAP,
    data?: NotificationDto,
    options?: NgbModalOptions
  ) {
    this.modalRef = this.modalService.openModalWithInjectData(BaseNotificationModalComponent, {
      modalComponent,
      data,
      ...options,
    });
    from(this.modalRef.result)
      .pipe(switchMap(() => this.setReadDate(data.id)))
      .subscribe();
  }

  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  startLiveClassPolling(classBegin: number): void {
    this.notificationListener.startLiveClassPolling(classBegin);
  }

  stopLiveClassPolling(): void {
    this.notificationListener.stopLiveClassPolling();
  }

  setReadDate(notificationId: number): Observable<Object> {
    if (notificationId === 0) {
      return of(null);
    }

    return this.http
      .post(
        `${this.oeApp.oeNotificationServiceHost}/notifications/confirm/${this.oeAppPersonService.id}/${notificationId}`,
        {},
        {
          responseType: 'text',
        }
      )
      .pipe(tap(() => this._readDateSet$.next(notificationId)));
  }

  setDeleteFlag(notificationId: number): Observable<Object> {
    if (notificationId === 0) {
      return of(null);
    }

    return this.http.put(
      `${this.oeApp.oeNotificationServiceHost}/notifications/delete/${notificationId}/${this.oeAppPersonService.id}`,
      {},
      {
        responseType: 'text',
      }
    );
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/notification.service.ts